import type {
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const control = 'w-full rounded-2xl bg-white/[0.05] px-4 text-[15px] text-cream ring-1 ring-white/10 transition-[background-color,box-shadow] duration-300 placeholder:text-cream/35 hover:ring-white/20 focus:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-citron/60 disabled:opacity-50'

interface FieldProps {
    id: string
    label: string
    hint?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

export const Field = ({ id, label, hint, optional, className, children }: FieldProps) => {
    return (
        <div className={className}>
            <label htmlFor={id} className='mb-2 flex items-baseline justify-between gap-3 text-[13px] text-cream/75'>
                <span>
                    {label}
                </span>

                {optional && (
                    <span className='text-[11px] text-cream/35'>
                        Optional
                    </span>
                )}
            </label>

            {children}

            {hint && (
                <p className='mt-1.5 text-xs text-cream/40'>
                    {hint}
                </p>
            )}
        </div>
    )
}

export const TextInput = ({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) => {
    return <input className={cn(control, 'h-12', className)} {...props} />
}

export const TextArea = ({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) => {
    return <textarea className={cn(control, 'min-h-[120px] resize-y py-3 leading-relaxed', className)} {...props} />
}

export const SelectInput = ({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) => {
    return (
        <div className='relative'>
            <select className={cn(control, 'h-12 cursor-pointer appearance-none pr-10 [&>option]:bg-neutral-900', className)} {...props}>
                {children}
            </select>

            <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cream/50' />
        </div>
    )
}

/** Hidden field that only bots fill in. Submissions that fill it are ignored. */
export const Honeypot = ({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
            <label htmlFor='website-field'>
                Leave this field empty
            </label>

            <input
                id='website-field'
                name='website'
                type='text'
                tabIndex={-1}
                autoComplete='off'
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </div>
    )
}

interface FormMessageProps {
    tone?: 'error' | 'success'
    children: ReactNode
    className?: string
}

export const FormMessage = ({ tone = 'error', children, className }: FormMessageProps) => {
    return (
        <p
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn(
                'rounded-2xl px-4 py-3 text-sm leading-relaxed',
                tone === 'error' ? 'bg-[#ff8a6b]/10 text-[#ffb59f] ring-1 ring-[#ff8a6b]/25' : 'bg-citron/10 text-citron ring-1 ring-citron/25',
                className
            )}
        >
            {children}
        </p>
    )
}
